import {
  normalizeDocumentType,
  type DocumentTemplate,
} from '@/entities/templates';

import {
  sanitizeSectionForSubmit,
  trimFields,
} from '@/features/templateEditor';

import type { NoteBlockDef, NoteLineDef } from './noteView';

const isEmptyText = (block: NoteBlockDef) =>
  block.Kind.case === 'Text' && !block.Kind.value.Value.trim();

const withoutEmptyLineText = (line: NoteLineDef): NoteLineDef => ({
  ...line,
  Blocks: line.Blocks.filter((block) => !isEmptyText(block)),
});

// What a block template saves: the editor keeps an empty wording where text
// can be typed, before, between and after the fields of a line and in a new
// line, but scribe takes no empty text block. A line left with no blocks
// holds nothing to save.
export const toSavedTemplateSections = (template: DocumentTemplate) =>
  template.sections.map((section) => ({
    ...section,
    Lines: section.Lines.map(withoutEmptyLineText).filter(
      (line) => line.Blocks.length > 0,
    ),
  }));

// The next active version of the source's series, prepared like the template
// editor's publish: the server then supersedes the previous active version and
// new encounters pin this one. The source document goes byte for byte: a
// partner template's blocks are checked against it.
export const toTemplateVersionRequest = ({
  template,
  source,
  organizationId,
  expectLatest = false,
}: {
  template: DocumentTemplate;
  source: DocumentTemplate;
  organizationId: string;
  // Scribe refuses the version when another one replaced the source
  // meanwhile, instead of replacing that one silently.
  expectLatest?: boolean;
}) => {
  const { source_document: sourceDocument, ...fields } = template;
  const trimmed = trimFields({
    ...fields,
    sections: toSavedTemplateSections(template),
  });
  const type = normalizeDocumentType(trimmed.type);
  return {
    code: trimmed.code,
    language: trimmed.language,
    type,
    name: trimmed.name,
    tab_title: trimmed.tab_title,
    description: trimmed.description,
    sections: trimmed.sections.map((section) =>
      sanitizeSectionForSubmit(section, type),
    ),
    source_document: sourceDocument,
    organization_id: source.organization_id || organizationId,
    status: 'active' as const,
    base_series_id: source.series_id,
    ...(expectLatest && { expected_latest_template_id: source.id }),
    source_kind: source.source_kind,
    derived_from_kind: source.derived_from_kind,
    derived_from_id: source.derived_from_id,
  };
};

// Keys whose value names an element of the template. Scribe gives a section,
// line or block saved without an id, or with a temporary one, an id of its
// own and renames what refers to it, so ids are compared by where they first
// appear.
const ELEMENT_ID_KEYS = ['ID', 'BlockID'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const listChildren = (value: unknown): [string, unknown][] => {
  if (Array.isArray(value)) {
    return value.map((item, index) => [String(index), item]);
  }
  return isRecord(value)
    ? Object.keys(value)
        .sort()
        .filter((key) => value[key] !== undefined)
        .map((key) => [key, value[key]])
    : [];
};

const isElementId = (key: string, value: unknown): value is string =>
  ELEMENT_ID_KEYS.includes(key) && typeof value === 'string';

const listElementIds = (
  value: unknown,
  path: string,
): { path: string; id: string }[] =>
  listChildren(value).flatMap(([key, child]) =>
    isElementId(key, child)
      ? [{ path: `${path}/${key}`, id: child }]
      : listElementIds(child, `${path}/${key}`),
  );

const toComparable = (
  value: unknown,
  path: string,
  firstPaths: Record<string, string>,
): unknown => {
  const children = listChildren(value).map(([key, child]): [string, unknown] =>
    isElementId(key, child)
      ? [key, (child && firstPaths[child]) || `${path}/${key}`]
      : [key, toComparable(child, `${path}/${key}`, firstPaths)],
  );
  if (Array.isArray(value)) {
    return children.map(([, child]) => child);
  }
  return isRecord(value) ? Object.fromEntries(children) : value;
};

const toVersionContent = (template: DocumentTemplate) => {
  const { name, sections } = trimFields({
    ...template,
    sections: toSavedTemplateSections(template),
  });
  const type = normalizeDocumentType(template.type);
  const content = {
    name,
    sections: sections
      .map((section) => sanitizeSectionForSubmit(section, type))
      .map(
        ({
          Code,
          Title,
          Instructions,
          Required,
          AllowAITextRewrite,
          Options,
          Lines,
        }) => ({
          Code,
          Title,
          Instructions,
          Required,
          // Scribe writes the permission out on block sections; unset is
          // not allowed.
          AllowAITextRewrite: !!AllowAITextRewrite,
          Options,
          Lines,
        }),
      ),
  };
  const firstPaths = listElementIds(content, '').reduce<Record<string, string>>(
    (paths, { path, id }) =>
      id && !(id in paths) ? { ...paths, [id]: path } : paths,
    {},
  );
  return JSON.stringify(toComparable(content, '', firstPaths));
};

// Whether a stored version holds what saving the template would store. A
// save whose answer was lost may have stored it: the next save of the same
// changes is then refused, as another version replaced the one it is based
// on, and that version is the one saved before.
export const holdsTemplateContent = (
  version: DocumentTemplate,
  template: DocumentTemplate,
) => toVersionContent(version) === toVersionContent(template);
